'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useState, ChangeEvent, FormEvent, useEffect } from 'react';

interface FormData {
  nom: string;
  prenom: string;
  email: string;
  telephone: string;
  sujet: string;
  message: string;
  rgpd: boolean;
  website: string;
}

interface FormStatus {
  isSubmitting: boolean;
  isSuccess: boolean;
  isError: boolean;
  message: string;
}

export default function ContactClient() {
  const [formData, setFormData] = useState<FormData>({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    sujet: '',
    message: '',
    rgpd: false,
    website: ''
  });

  const [loadTime, setLoadTime] = useState<number>(0);

  useEffect(() => {
    setLoadTime(Date.now());
  }, []);

  const [formStatus, setFormStatus] = useState<FormStatus>({
    isSubmitting: false,
    isSuccess: false,
    isError: false,
    message: ''
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const isSpam = (data: FormData): boolean => {
    if (data.website.length > 0) {
      return true;
    }

    if (Date.now() - loadTime < 5000) {
      return true;
    }

    const spamPatterns = [
      /\b(?:viagra|cialis|casino|crypto|forex|buy followers)\b/i,
      /https?:\/\/\S+/gi,
      /\[url=/i
    ];

    if (spamPatterns.some(pattern => pattern.test(data.message))) {
      return true;
    }

    return false;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isSpam(formData)) {
      console.log('Spam submission detected, silently rejected');
      setFormStatus({
        isSubmitting: false,
        isSuccess: true,
        isError: false,
        message: 'Votre message a été envoyé avec succès. Nous vous répondrons dans les plus brefs délais.'
      });
      return;
    }

    setFormStatus({
      isSubmitting: true,
      isSuccess: false,
      isError: false,
      message: ''
    });

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Une erreur est survenue lors de l\'envoi du message.');
      }

      setFormStatus({
        isSubmitting: false,
        isSuccess: true,
        isError: false,
        message: 'Votre message a été envoyé avec succès. Nous vous répondrons dans les plus brefs délais.'
      });

      setFormData({
        nom: '',
        prenom: '',
        email: '',
        telephone: '',
        sujet: '',
        message: '',
        rgpd: false,
        website: ''
      });
    } catch (error: unknown) {
      setFormStatus({
        isSubmitting: false,
        isSuccess: false,
        isError: true,
        message: error instanceof Error ? error.message : 'Une erreur est survenue lors de l\'envoi du message.'
      });
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      {/* Section Hero */}
      <section className="hero-section h-[60vh] flex items-center">
        <Image
          src="/images/uploads/IMG_4035.jpg"
          alt="Contact - Maître Marie-Camille Roch"
          fill
          className="hero-image"
          priority
        />
        <div className="hero-overlay"></div>
        <div className="container-custom hero-content">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-center">
            CONTACTEZ LE CABINET
          </h1>
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-lg mb-6">
              Pour toute demande d'information ou prise de rendez-vous, n'hésitez pas à contacter Maître Marie-Camille Roch.
            </p>
          </div>
        </div>
      </section>

      {/* Section Contact */}
      <section className="py-16">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Coordonnées */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-8">Coordonnées</h2>

              <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                <div className="flex items-start mb-6">
                  <div className="bg-primary/10 p-3 rounded-full mr-4">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-primary">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Adresse</h3>
                    <p className="text-gray-700">24 Rue Durand</p>
                    <p className="text-gray-700">34000 Montpellier</p>
                  </div>
                </div>

                <div className="flex items-start mb-6">
                  <div className="bg-primary/10 p-3 rounded-full mr-4">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-primary">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Téléphone</h3>
                    <p className="text-gray-700">
                      <a href="tel:+33608357017" className="hover:text-primary transition-colors">06 08 35 70 17</a>
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-primary/10 p-3 rounded-full mr-4">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-primary">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Horaires</h3>
                    <p className="text-gray-700">Du lundi au vendredi</p>
                    <p className="text-gray-700">9h30 - 20h00</p>
                    <p className="text-gray-700 mt-2">Samedi et dimanche fermé</p>
                    <p className="text-gray-700 text-sm italic">(possibilité de rendez-vous en urgence)</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Formulaire */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-8">Formulaire de contact</h2>

              <div className="bg-white p-6 rounded-lg shadow-md">
                {formStatus.isSuccess ? (
                  <div className="bg-green-100 text-green-800 p-4 rounded-md mb-6">
                    <p className="font-medium">{formStatus.message}</p>
                  </div>
                ) : (
                  <form className="space-y-6" onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="nom" className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                        <input
                          type="text"
                          id="nom"
                          name="nom"
                          value={formData.nom}
                          onChange={handleChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="prenom" className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label>
                        <input
                          type="text"
                          id="prenom"
                          name="prenom"
                          value={formData.prenom}
                          onChange={handleChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          required
                        />
                      </div>
                    </div>

                    <div className="opacity-0 absolute top-0 left-0 h-0 w-0 -z-10 overflow-hidden">
                      <label htmlFor="website">Website</label>
                      <input
                        type="text"
                        id="website"
                        name="website"
                        value={formData.website}
                        onChange={handleChange}
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="telephone" className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                      <input
                        type="tel"
                        id="telephone"
                        name="telephone"
                        value={formData.telephone}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label htmlFor="sujet" className="block text-sm font-medium text-gray-700 mb-1">Sujet *</label>
                      <select
                        id="sujet"
                        name="sujet"
                        value={formData.sujet}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        required
                      >
                        <option value="">Sélectionnez un sujet</option>
                        <option value="droit-penal">Droit pénal</option>
                        <option value="effacement-fichiers">Effacement de fichiers</option>
                        <option value="prejudice-corporel">Dommage corporel</option>
                        <option value="autre">Autre</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                      <textarea
                        id="message"
                        name="message"
                        rows={5}
                        value={formData.message}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        required
                      ></textarea>
                    </div>

                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        id="rgpd"
                        name="rgpd"
                        checked={formData.rgpd}
                        onChange={handleChange}
                        className="mt-1 mr-2"
                        required
                      />
                      <label htmlFor="rgpd" className="text-sm text-gray-700">
                        J'accepte que mes données soient traitées conformément à la politique de confidentialité du cabinet. *
                      </label>
                    </div>

                    {formStatus.isError && (
                      <div className="bg-red-100 text-red-800 p-4 rounded-md">
                        <p className="font-medium">{formStatus.message}</p>
                      </div>
                    )}

                    <div className="text-center">
                      <button
                        type="submit"
                        className="bg-primary text-white hover:bg-primary/90 px-6 py-3 rounded-md transition-colors inline-block font-medium"
                        disabled={formStatus.isSubmitting}
                      >
                        {formStatus.isSubmitting ? 'Envoi en cours...' : 'Envoyer'}
                      </button>
                    </div>

                    <p className="text-sm text-gray-500 text-center">
                      * Champs obligatoires
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Carte */}
      <section className="py-16 bg-secondary/10">
        <div className="container-custom">
          <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">Localisation</h2>

          <div className="bg-white p-4 rounded-lg shadow-md">
            <div className="aspect-video w-full">
              <iframe
                title="Carte indiquant l'emplacement du cabinet"
                src="https://www.google.com/maps?q=24+Rue+Durand,+34000+Montpellier&hl=fr&z=16&output=embed"
                width="100%"
                height="100%"
                style={{border: 0}}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="rounded-lg"
              ></iframe>
            </div>
          </div>

          <div className="mt-8 bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-bold mb-4">Accès</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start">
                <div className="bg-primary/10 p-3 rounded-full mr-4">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-primary">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 0 1-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 0 0-3.213-9.193 2.056 2.056 0 0 0-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 0 0-10.026 0 1.106 1.106 0 0 0-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-bold mb-1">En voiture</h4>
                  <p className="text-gray-700">Parking des Arceaux à proximité</p>
                  <p className="text-gray-700">Parking Comédie à 5 minutes à pied</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="bg-primary/10 p-3 rounded-full mr-4">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-primary">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-bold mb-1">En transports en commun</h4>
                  <p className="text-gray-700">Tramway ligne 1 et 2 - Arrêt Comédie</p>
                  <p className="text-gray-700">Bus - Arrêt Comédie</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
